import React from "react";
import { useSelector } from "react-redux";

import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import {
  Grid,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Box,
  Chip,
  Checkbox,
} from "@mui/material";

import FormTextField from "components/FormFields/FormTextField";
import FormFieldError from "components/common/form_error/form_field_error";

import { getRpmDeviceKeyFromValue } from "utils/rpmDevice.util";
import {
  setPatientInfoToUpdate,
  setShowReportTemplateUpdateAlert,
} from "app/store/manualUpload/slice/manualUploadSlice";
import { useDispatch } from "react-redux";
import FormSelectField from "components/FormFields/FormSelect";
import useAuthorization from "hooks/useAuthorization";
import { manufacturerMappings } from "static/constants";

const DeviceInformationInput = ({ fields, onUpdate, isError, devices }) => {
  const { isSubmitting, isSubmitClicked, reportTemplates } = useSelector(
    (state) => state.manualUpload
  );

  const dispatch = useDispatch();
  const filter = createFilterOptions();
  const { accessedAccountMappings } = useAuthorization();
  const { currentUser } = useSelector((state) => state.auth);

  const getManufacturers = (isAddtionalRPMManufacturer) => {
    if (!currentUser) {
      return manufacturerMappings;
    }
    const { manufacturer: currentUserManufacturers } = currentUser;
    const validManufacturers = manufacturerMappings.filter((manufacturer) =>
      currentUserManufacturers.includes(manufacturer.name)
    );

    const filteredManufacturers = isAddtionalRPMManufacturer
      ? validManufacturers?.filter(
          (manufacturer) =>
            manufacturer?.rpmDevice && manufacturer.name !== fields.manufacturer
        )
      : validManufacturers;

    return filteredManufacturers;
  };

  return (
    <Grid container item spacing={2}>
      <Grid item xs={12}>
        <h2 data-label="device-information-header">Device Information</h2>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormSelectField
              label="Account"
              required={true}
              margin="none"
              submitClicked={isSubmitClicked}
              hasError={isError("account") && isSubmitClicked}
              customMapping={
                <Select
                  style={{ minWidth: "120px" }}
                  id="state"
                  value={fields.account}
                  onChange={(e) => {
                    if (reportTemplates.length > 0) {
                      dispatch(setShowReportTemplateUpdateAlert(true));
                      dispatch(
                        setPatientInfoToUpdate({
                          key: "account",
                          newValue: e.target.value,
                          currentValue: fields.account,
                        })
                      );
                    } else {
                      onUpdate({ account: e.target.value });
                    }
                  }}
                  data-action="device-information-account-select"
                >
                  {accessedAccountMappings?.map((account, index) => {
                    return (
                      <MenuItem
                        key={account?._id}
                        value={account?.name}
                        style={account?.hide ? { display: "none" } : null}
                        data-action={`device-information-account-option-${account?.name}`}
                      >
                        {account?.name}
                      </MenuItem>
                    );
                  })}
                </Select>
              }
              onChange={(e) => {
                onUpdate({ account: e.target.value });
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <FormSelectField
              label="Manufacturer"
              required={true}
              margin="none"
              options={getManufacturers()}
              submitClicked={isSubmitClicked}
              hasError={isError("manufacturer") && isSubmitClicked}
              customMapping={
                <Select
                  value={fields.manufacturer}
                  onChange={(e) => {
                    if (reportTemplates.length > 0) {
                      dispatch(setShowReportTemplateUpdateAlert(true));
                      dispatch(
                        setPatientInfoToUpdate({
                          key: "manufacturer",
                          newValue: e.target.value,
                          currentValue: fields.manufacturer,
                        })
                      );
                    } else {
                      onUpdate({ manufacturer: e.target.value });
                    }
                  }}
                  data-action="device-information-manufacturer-select"
                >
                  {getManufacturers().map((manufacturer, index) => (
                    <MenuItem
                      key={index}
                      value={manufacturer.name}
                      data-action={`device-information-manufacturer-option-${manufacturer.name}`}
                    >
                      {manufacturer.name}
                    </MenuItem>
                  ))}
                </Select>
              }
            />
          </Grid>

          {fields.isRpmDevice && (
            <Grid item xs={12}>
              <FormSelectField
                margin="none"
                label="Additional Manufacturer"
                options={getManufacturers(true)}
                submitClicked={isSubmitClicked}
                hasError={isError("additionalManufacturer") && isSubmitClicked}
                customMapping={
                  <Select
                    multiple
                    style={{ minWidth: "120px" }}
                    id="state"
                    value={fields.additionalManufacturer}
                    renderValue={(selected) => (
                      <Box>
                        {selected.map((manufacturer) => (
                          <Chip
                            key={manufacturer}
                            label={manufacturer}
                            className="mr-1"
                          />
                        ))}
                      </Box>
                    )}
                    onChange={(e) => {
                      onUpdate({ additionalManufacturer: e.target.value });
                    }}
                    data-action="device-information-additional-manufacturer-select"
                  >
                    {getManufacturers(true).map((manufacturer, index) => (
                      <MenuItem
                        key={index}
                        value={manufacturer.name}
                        data-action={`device-information-additional-manufacturer-option-${manufacturer.name}`}
                      >
                        <Checkbox
                          checked={
                            fields.additionalManufacturer.indexOf(
                              manufacturer.name
                            ) > -1
                          }
                          data-action={`device-information-additional-manufacturer-checkbox-${manufacturer.name}`}
                        />
                        {manufacturer.name}
                      </MenuItem>
                    ))}
                  </Select>
                }
              />
            </Grid>
          )}

          {fields.isRpmDevice ? (
            <Grid item xs={12} spacing={2}>
              <h2 data-label="device-information-rpm-device-header">
                RPM Device
              </h2>
              {[fields.manufacturer, ...fields.additionalManufacturer].map(
                (manufacturer) => {
                  const rpmDeviceKey = getRpmDeviceKeyFromValue(manufacturer);
                  const deviceImei =
                    fields.rpmDevices[rpmDeviceKey]?.imei || "";

                  return (
                    <Grid key={manufacturer} container spacing={2}>
                      <Grid item xs={3} className="gridItem">
                        <span className="gridItem-title">RPM Device Type</span>
                        <span className="gridItem-subTitle">
                          {manufacturer}
                        </span>
                      </Grid>

                      <Grid item xs={9}>
                        <FormTextField
                          fullWidth
                          required={true}
                          value={deviceImei}
                          label="IMEI"
                          onInput={(event) => {
                            const input = event.target;
                            if (input.value.length > 50) {
                              input.value = input.value.slice(0, 50); // Truncate extra characters
                            }
                          }}
                          onChange={(e) => {
                            const imei = e.target.value;

                            if (/^\d*$/.test(imei)) {
                              const newValue = {
                                manufacturer,
                                imei: imei?.trim(),
                                active: true,
                              };
                              fields.rpmDevices[rpmDeviceKey] = newValue;
                              onUpdate({ rpmDevices: fields.rpmDevices });
                            }
                          }}
                          hasError={!deviceImei?.trim() && isSubmitClicked}
                          data-text={`device-information-rpm-imei-${rpmDeviceKey}-field`}
                        />
                      </Grid>
                    </Grid>
                  );
                }
              )}
            </Grid>
          ) : (
            <>
              <Grid item xs={6}>
                <FormControl style={{ width: "100%", marginTop: "16px" }}>
                  <Autocomplete
                    name="modelName"
                    fullWidth
                    clearOnBlur
                    selectOnFocus
                    className="mb-3"
                    handleHomeEndKeys
                    id="free-solo-with-text-demo"
                    value={
                      fields?.modelName
                        ? devices?.find(
                            (item) =>
                              item?.modelDisplayName === fields?.modelName
                          )
                        : null
                    }
                    options={
                      devices?.filter((item) => item?.modelDisplayName) || []
                    }
                    getOptionLabel={(option) => option?.modelDisplayName}
                    filterOptions={(options, params) => {
                      const filtered = filter(options, params);
                      return filtered;
                    }}
                    onChange={(event, newValue) => {
                      const selectedModelName =
                        newValue?.modelDisplayName || "";

                      if (reportTemplates.length > 0) {
                        dispatch(setShowReportTemplateUpdateAlert(true));
                        dispatch(
                          setPatientInfoToUpdate({
                            key: "modelName",
                            newValue: selectedModelName,
                            currentValue: fields?.modelName,
                          })
                        );
                      } else {
                        onUpdate({ modelName: selectedModelName });
                      }
                    }}
                    data-action="device-information-model-name-autocomplete"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        error={isError("modelName") && isSubmitClicked}
                        label="Model Name"
                        variant="outlined"
                        placeholder="Model Name"
                        InputProps={{
                          ...params.InputProps,
                          readOnly: isSubmitting ? true : false,
                        }}
                        data-text="device-information-model-name-field"
                      />
                    )}
                  />
                  {isError("modelName") && isSubmitClicked && (
                    <FormFieldError errorMessage={`Model Name is required.`} />
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <FormTextField
                  fullWidth
                  value={fields.modelNumber}
                  label="Serial Number"
                  onChange={(e) => {
                    const regex = /^[A-Za-z0-9]*$/;
                    if (e.target.value === "" || regex.test(e.target.value)) {
                      onUpdate({ modelNumber: e.target.value });
                    }
                  }}
                  required={true}
                  hasError={isError("modelNumber") && isSubmitClicked}
                  inputProps={{ maxLength: 50 }}
                  data-text="device-information-serial-number-field"
                />
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default DeviceInformationInput;
